// MAP CLASS — the 2D Map's picture, painted ON THE DEVICE (gs-delete-photo; PROTOCOL.md "Map
// pack"): the bake ships no Map imagery. The full Map is a LAND-COVER map of the data the client
// already has: the class map (meta.surface.coarse: 4 m, meta.surface.tiles: 1 m 512 m tiles, the
// class id in R) coloured by the genome (surface/genome.json: per class mean_lin + its elev / slope
// Lab terms, ringref classMean — never re-typed here), shaded by the GROUND-M skin's 16 m posts
// (mapheights.js MapHeights: groundAt on one groundm level, tol <= 4 m — no terrain level; a
// LOOK change against the old 5x5-min envelope, the owner's look decides) (Lambert n.sun of the
// bake's sun, meta.lighting; E = groundref
// lightIrradiance(world, lam, sky 1, n_y) — no cast shadows), sRGB through groundref linToSrgb8
// (E is / meta.tex.light.white: the Map is toneMapped:false UI). Roads, water, trails stay vector
// layers on top (maplayers.js). PURE (no DOM): run by the Map's worker (mapwork.js) or, with no
// worker, on the main thread in BAND-row slices (maptiles.js MapClass); scripts/check-view.mjs
// imports it.
//
//   texel   per class id: genome record (classMean(rec, Lab(mean_lin), h, slope deg) x anchor)
//           | painterpal base (linear; no record: snow, ...) | water 254: painterpal water.deep
//           (the lakes / rivers draw over it); none 255 and unknown ids paint as meadow
//           (groundref's rule). x the residual 2^((code - 128) / RES_PER_STOP) (meta.surface
//           .residual, 16 m bilinear) on the TILES only (opts.res); the measured S2 colour on
//           painterpal measured_keys (groundref prepWorld's W.s2 rule: lin x cov, bilinear,
//           measuredLin(lin / cw) blended by cw) on the underlay refine and the tiles.
//   light   h / gx / gz bilinear on the 16 m posts (central differences); lam = max(0, n.L),
//           water flat (lam = sin alt, n_y 1); no heights: flat.
//   jobs    underlayJob(coarse, s): every s-th coarse texel (texel s x 4 m; FIRST_STRIDE 4 = 16 m
//           first, then the device's tunables map.underlay_m: phone 8 m, other 4 m);
//           tileJob(cls, tx, ty, tileM): one 512 m class tile -> 512 px. paintRows(W, J, k0, k1)
//           paints rows [k0, k1) of J.out (RGBA, row 0 = NORTH).
//   core    mapCore(env): the op handler mapwork.js and the main-thread path share — world,
//           coarse, s2, res (inputs), underlay (RESUMABLE: a 'pause' between bands keeps the job,
//           the next 'underlay' of the same stride goes on from its row), tile.
//   zoom    tilesOn(mpp, uM): tiles iff mpp x TEXEL_SLACK < the device's underlay texel (the
//           fit-zoom overview paints none); one grid, meta.surface.tiles (tileRange / tilesWanted
//           clipped to the real extent and the grid's nx / ny), keys `2/<tx>_<ty>`.
import { lab, linToSrgb8, lightIrradiance, measuredLin, RES_PER_STOP } from './groundref.js';
import { classMean } from './ringref.js';

export const TILE_PX = 512;
export const MIN_MPP = 0.125;         // the Map's zoom floor (m per canvas px; a 1 m class tile over-zooms 8x)
export const TEXEL_SLACK = 1.25;      // a texel may span this many canvas px
export const CAP = 300;               // LRU tile bitmaps (desktop; the phone passes less)
export const MAX_LOADS = 6;
export const FIRST_STRIDE = 4;        // the first underlay: every 4th coarse texel (16 m/px)
export const BAND = 64;               // rows per paint band (a yield / a pause between)
export const GRASS_SAT_DEFAULT = 2.0; // surfacegen GRASS_SAT (groundgen: meta.tex.s2 null)

export const isPhone = (cls) => cls === 'phone-lo' || cls === 'phone-hi';
// the device's underlay texel (m): tunables map.underlay_m {phone, other}
export function underlayM(cls, cfg) {
  const u = cfg && cfg.underlay_m;
  if (!(u && u.phone > 0 && u.other > 0)) throw new Error('tunables map.underlay_m {phone, other} missing');
  return isPhone(cls) ? u.phone : u.other;
}
export const tilesOn = (mpp, uM) => mpp * TEXEL_SLACK < uM;
export const tileKey = (tx, ty) => `2/${tx}_${ty}`;

// world AABB [x0, z0, x1, z1] of a W×H canvas showing view {cx, cz, mpp, rot}
export function viewRect(v, W, H) {
  const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const [u, w] of [[-W / 2, -H / 2], [W / 2, -H / 2], [W / 2, H / 2], [-W / 2, H / 2]]) {
    const x = v.cx + (u * c - w * s) * v.mpp, z = v.cz + (u * s + w * c) * v.mpp;
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (z < z0) z0 = z; if (z > z1) z1 = z;
  }
  return [x0, z0, x1, z1];
}

// tile index range over rect, clipped to the real extent and the grid
// (meta.surface.tiles {tile_m, nx, ny}); null = nothing (off the world)
export function tileRange(rect, ex, ez, grid) {
  const T = grid.tile_m;
  const nx = Math.min(Math.ceil(ex / T), grid.nx), ny = Math.min(Math.ceil(ez / T), grid.ny);
  const x0 = Math.max(0, rect[0]), z0 = Math.max(0, rect[1]);
  const x1 = Math.min(ex, rect[2]), z1 = Math.min(ez, rect[3]);
  if (x1 <= x0 || z1 <= z0 || nx <= 0 || ny <= 0) return null;
  const tx0 = Math.floor(x0 / T), ty0 = Math.floor(z0 / T);
  const tx1 = Math.min(nx - 1, Math.floor((x1 - 1e-6) / T)), ty1 = Math.min(ny - 1, Math.floor((z1 - 1e-6) / T));
  if (tx1 < tx0 || ty1 < ty0) return null;
  return { T, tx0, ty0, tx1, ty1 };
}

// the wanted tiles, nearest (tile centre → view centre) first
export function tilesWanted(v, W, H, ex, ez, grid) {
  const r = tileRange(viewRect(v, W, H), ex, ez, grid);
  if (!r) return [];
  const out = [];
  for (let ty = r.ty0; ty <= r.ty1; ty++) {
    for (let tx = r.tx0; tx <= r.tx1; tx++) {
      const d = Math.hypot((tx + 0.5) * r.T - v.cx, (ty + 0.5) * r.T - v.cz);
      out.push({ tx, ty, key: tileKey(tx, ty), d });
    }
  }
  return out.sort((a, b) => a.d - b.d || a.ty - b.ty || a.tx - b.tx);
}

// ---------------------------------------------------------------- the world
const srgbDec = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
// bilinear of channel ch (nc per cell) of a w × h grid at (k + 0.5) cell, clamped
function bil(G, w, h, nc, ch, cell, x, z) {
  const u = x / cell - 0.5, v = z / cell - 0.5, fu = Math.floor(u), fv = Math.floor(v);
  const i0 = clamp(fu, 0, w - 1), i1 = clamp(fu + 1, 0, w - 1), j0 = clamp(fv, 0, h - 1), j1 = clamp(fv + 1, 0, h - 1);
  const tx = clamp(u - fu, 0, 1), tz = clamp(v - fv, 0, 1);
  const a = G[(j0 * w + i0) * nc + ch], b = G[(j0 * w + i1) * nc + ch], c = G[(j1 * w + i0) * nc + ch], d = G[(j1 * w + i1) * nc + ch];
  return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
}

// inp = { classes (meta.surface.classes [{id, key}]), waterId, genome (genome.json), pal
//   (painterpal.json), light (meta.tex.light), sunAltDeg, sunAzDeg (meta.lighting), anchor,
//   grassSat, heights {a Float32Array w h (post (i + 0.5) cell), w, h, cell} | null,
//   res {w, h, cell, x0, z0} | null (the residual grid's geometry; resPut fills it),
//   s2 {cell, x0, z0} | null (its geometry; s2Put fills it) }
export function mapWorld(inp) {
  const recs = (inp.genome && inp.genome.classes) || {}, P = inp.pal.palette, meas = new Set(inp.pal.measured_keys);
  const keyOf = new Map(inp.classes.map((c) => [c.id, c.key]));
  const W = { light: inp.light, waterId: inp.waterId, anchor: inp.anchor, grassSat: inp.grassSat,
    rec: new Array(256).fill(null), L0: new Array(256).fill(null), pal: new Array(256).fill(null), meas: new Uint8Array(256),
    water: P.water.deep.map((v) => srgbDec(v / 255)), hgt: null, res: null, s2: null, s2g: inp.s2 || null };
  for (let id = 0; id < 256; id++) {
    if (id === inp.waterId) continue;
    const k = keyOf.get(id) || 'meadow', r = recs[k];
    if (r) { W.rec[id] = r; W.L0[id] = lab(r.mean_lin[0], r.mean_lin[1], r.mean_lin[2]); }
    else W.pal[id] = (P[k] || P.meadow).base.map((v) => srgbDec(v / 255));
    W.meas[id] = meas.has(k) ? 1 : 0;
  }
  const az = inp.sunAzDeg * Math.PI / 180, alt = inp.sunAltDeg * Math.PI / 180;
  W.sun = { sx: Math.sin(az) * Math.cos(alt), sy: Math.cos(az) * Math.cos(alt), sz: Math.sin(alt) };
  const H = inp.heights;
  if (H) {
    const { a, w, h, cell } = H, gx = new Float32Array(w * h), gz = new Float32Array(w * h);
    for (let k = 0; k < h; k++) {
      for (let i = 0; i < w; i++) {
        const il = Math.max(0, i - 1), ir = Math.min(w - 1, i + 1), ku = Math.max(0, k - 1), kd = Math.min(h - 1, k + 1);
        gx[k * w + i] = ir > il ? (a[k * w + ir] - a[k * w + il]) / ((ir - il) * cell) : 0;
        gz[k * w + i] = kd > ku ? (a[kd * w + i] - a[ku * w + i]) / ((kd - ku) * cell) : 0;
      }
    }
    W.hgt = { a, gx, gz, w, h, cell };
  }
  if (inp.res) W.res = { ...inp.res, codes: new Uint8Array(inp.res.w * inp.res.h * 3).fill(128) };
  return W;
}
// the S2 colour image {data Uint8Array w h 3, w, h} (0 0 0 = none) -> W.s2 (lin x cov, cov)
export function s2Put(W, d) {
  const g = W.s2g, n = d.w * d.h, lin = new Float32Array(n * 3), cov = new Float32Array(n);
  for (let q = 0; q < n; q++) {
    const on = d.data[3 * q] || d.data[3 * q + 1] || d.data[3 * q + 2] ? 1 : 0;
    cov[q] = on;
    for (let c = 0; c < 3; c++) lin[3 * q + c] = srgbDec(d.data[3 * q + c] / 255) * on;
  }
  W.s2 = { lin, cov, w: d.w, h: d.h, cell: g.cell, x0: g.x0, z0: g.z0 };
}
// one residual tile (tx, ty) of px cells {data Uint8Array w h 3, w, h} into W.res.codes
export function resPut(W, tx, ty, px, d) {
  const R = W.res;
  for (let k = 0; k < d.h; k++) {
    const Z = ty * px + k;
    if (Z >= R.h) break;
    for (let i = 0; i < d.w; i++) {
      const X = tx * px + i;
      if (X >= R.w) break;
      for (let c = 0; c < 3; c++) R.codes[3 * (Z * R.w + X) + c] = d.data[3 * (k * d.w + i) + c];
    }
  }
}

// the linear albedo of class cid at (x, z), height h, slope sdeg (opts: res, noS2)
export function albedoLin(W, cid, x, z, h, sdeg, opts = {}) {
  if (cid === W.waterId) return W.water.slice();
  const rec = W.rec[cid];
  const b = rec ? (rec.elev && rec.slope ? classMean(rec, W.L0[cid], h, sdeg) : rec.mean_lin.slice()) : W.pal[cid].slice();
  const R = opts.res ? W.res : null;
  if (R) for (let c = 0; c < 3; c++) b[c] *= 2 ** ((bil(R.codes, R.w, R.h, 3, c, R.cell, x - R.x0, z - R.z0) - 128) / RES_PER_STOP);
  if (rec) for (let c = 0; c < 3; c++) b[c] *= W.anchor;
  const S = W.s2;
  if (S && W.meas[cid] && !opts.noS2) {
    const sx = x - S.x0, sz = z - S.z0, cw = bil(S.cov, S.w, S.h, 1, 0, S.cell, sx, sz);
    if (cw > 1e-6) {
      const m = measuredLin(bil(S.lin, S.w, S.h, 3, 0, S.cell, sx, sz) / cw, bil(S.lin, S.w, S.h, 3, 1, S.cell, sx, sz) / cw,
        bil(S.lin, S.w, S.h, 3, 2, S.cell, sx, sz) / cw, W.anchor, W.grassSat);
      for (let c = 0; c < 3; c++) b[c] = cw * m[c] + (1 - cw) * b[c];
    }
  }
  return b;
}

// one texel: class cid at (x, z) -> RGBA8 sRGB into out[o..o+3]
export function texel(W, cid, x, z, opts, out, o) {
  const H = W.hgt;
  let lam = W.sun.sz, ny = 1, h = 0, sdeg = 0;
  if (H) {
    h = bil(H.a, H.w, H.h, 1, 0, H.cell, x, z);
    if (cid !== W.waterId) {
      const gx = bil(H.gx, H.w, H.h, 1, 0, H.cell, x, z), gz = bil(H.gz, H.w, H.h, 1, 0, H.cell, x, z), g2 = gx * gx + gz * gz;
      const n = Math.sqrt(g2 + 1);
      lam = clamp((gx * -W.sun.sx + gz * W.sun.sy + W.sun.sz) / n, 0, 1);
      ny = 1 / n;
      sdeg = Math.atan(Math.sqrt(g2)) * 180 / Math.PI;
    }
  }
  const a = albedoLin(W, cid, x, z, h, sdeg, opts), E = lightIrradiance(W, lam, 1, ny);
  out[o] = linToSrgb8(a[0] * E[0]); out[o + 1] = linToSrgb8(a[1] * E[1]); out[o + 2] = linToSrgb8(a[2] * E[2]); out[o + 3] = 255;
}

// ---------------------------------------------------------------- jobs
// J = {cls, cw (class raster width), s (class texels per out texel), w, h, texel (m), x0, z0, out, k, opts}
export function underlayJob(coarse, s) {
  const w = Math.ceil(coarse.w / s), h = Math.ceil(coarse.h / s);
  return { cls: coarse.data, cw: coarse.w, s, w, h, texel: coarse.cell * s, x0: 0, z0: 0,
    out: new Uint8ClampedArray(w * h * 4), k: 0, opts: {} };
}
export function tileJob(cls, tx, ty, tileM, opts = {}) {
  return { cls: cls.data, cw: cls.w, s: 1, w: cls.w, h: cls.h, texel: tileM / cls.w, x0: tx * tileM, z0: ty * tileM,
    out: new Uint8ClampedArray(cls.w * cls.h * 4), k: 0, opts: { res: true, ...opts } };
}
export function paintRows(W, J, k0, k1) {
  for (let k = k0; k < k1; k++) {
    const z = J.z0 + (k + 0.5) * J.texel, row = k * J.s * J.cw;
    for (let i = 0; i < J.w; i++) texel(W, J.cls[row + i * J.s], J.x0 + (i + 0.5) * J.texel, z, J.opts, J.out, 4 * (k * J.w + i));
  }
}

// ---------------------------------------------------------------- the core (mapwork.js / the main thread)
// env = { pause(): a promise between bands (a task hop / sched slice), bitmap(rgba, w, h): a
// promise of the drawable }. handle(m) -> promise of {out, img?}. Ops:
//   world {inp}  coarse {data, w, h, cell}  s2 {data, w, h}  res {tx, ty, px, data, w, h}
//   underlay {s, keep} -> {img, w, h, texel} | {paused: true} (keep: the resumable job)   pause (the
//   kept underlay stops at its next band, the job kept)   tile {tx, ty, tileM, data, w, h} -> {img}
export function mapCore(env) {
  let W = null, coarse = null, ul = null, hold = false;
  const need = (x, what) => { if (!x) throw new Error(`map core: ${what} first`); return x; };
  async function run(J, stop) {
    while (J.k < J.h) {
      if (stop && stop()) return false;
      const k1 = Math.min(J.h, J.k + BAND);
      paintRows(W, J, J.k, k1);
      J.k = k1;
      if (J.k < J.h) await env.pause();
    }
    return true;
  }
  return {
    async handle(m) {
      if (m.op === 'world') { W = mapWorld(m.inp); ul = null; return {}; }
      if (m.op === 'coarse') { coarse = { data: m.data, w: m.w, h: m.h, cell: m.cell }; ul = null; return {}; }
      if (m.op === 's2') { s2Put(need(W, 'world'), m); return {}; }
      if (m.op === 'res') { resPut(need(W, 'world'), m.tx, m.ty, m.px, m); return {}; }
      if (m.op === 'pause') { hold = true; return {}; }
      if (m.op === 'underlay') {
        need(W, 'world'); need(coarse, 'coarse');
        let J;
        if (m.keep) {                         // the resumable one (the refine)
          if (!ul || ul.s !== m.s) ul = underlayJob(coarse, m.s);
          J = ul; hold = false;
        } else J = underlayJob(coarse, m.s);
        if (!(await run(J, m.keep ? () => hold : null))) return { paused: true };
        if (ul === J) ul = null;
        return { img: await env.bitmap(J.out, J.w, J.h), w: J.w, h: J.h, texel: J.texel };
      }
      if (m.op === 'tile') {
        need(W, 'world');
        const J = tileJob(m, m.tx, m.ty, m.tileM);
        await run(J, null);
        return { img: await env.bitmap(J.out, J.w, J.h) };
      }
      throw new Error(`map core: unknown op ${m.op}`);
    },
  };
}
